import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

const PricingSection = () => {
  const volumePricing = [
    {
      range: '1-100 Reservations',
      price: '$0.75',
      description: 'Perfect for new businesses',
      isPopular: false
    },
    {
      range: '101-300 Reservations',
      price: '$0.60',
      description: 'Most common for growing businesses',
      isPopular: true
    },
    {
      range: '301-1000 Reservations',
      price: '$0.45',
      description: 'For established businesses',
      isPopular: false
    },
    {
      range: '1000+ Reservations',
      price: '$0.35',
      description: 'For large operations',
      isPopular: false
    }
  ];

  const features = [
    'Online booking system',
    'Stripe payment integration',
    'Member management',
    'Advanced scheduling system',
    'Custom membership tiers',
    'Analytics & reporting',
    'Email & phone support'
  ];

  return (
    <Box py={{ base: 16, md: 20 }} bg="#110F14">
      <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
        <VStack spacing={8} textAlign="center" mb={16}>
          <Heading
            as="h2"
            fontSize={{ base: '3xl', md: '4xl' }}
            color="white"
            fontWeight="bold"
          >
            Simple Volume-Based Pricing
          </Heading>
          <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.400" maxW="2xl">
            One platform, all features included. Pay less per reservation as you grow.
          </Text>
          <Box maxW="3xl" textAlign="left" color="gray.400" fontSize="md">
            <Text mb={4}>
              Get access to all features regardless of your volume. Our pricing is simple: the more reservations you process, the less you pay per reservation.
            </Text>
            <Text>
              For example, if you process 250 reservations in a month, you'll pay $0.60 per reservation, totaling $150 for that month.
            </Text>
          </Box>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} mb={16}>
          {volumePricing.map((tier) => (
            <Box
              key={tier.range}
              bg="#1A1824"
              border="1px solid"
              borderColor={tier.isPopular ? '#82ca9c' : 'whiteAlpha.100'}
              borderRadius="2xl"
              p={8}
              position="relative"
              transform={tier.isPopular ? { md: 'scale(1.05)' } : 'none'}
              zIndex={tier.isPopular ? 1 : 0}
            >
              {tier.isPopular && (
                <Badge
                  colorScheme="green"
                  bg="#82ca9c"
                  color="white"
                  position="absolute"
                  top="-4"
                  right="-4"
                  px={4}
                  py={2}
                  borderRadius="full"
                  fontSize="sm"
                >
                  Most Common
                </Badge>
              )}
              
              <VStack spacing={6} align="stretch">
                <VStack spacing={2} align="stretch">
                  <Text color="gray.400" fontSize="lg">
                    {tier.range}
                  </Text>
                  <HStack spacing={1} align="flex-end">
                    <Text fontSize="4xl" fontWeight="bold" color="white">
                      {tier.price}
                    </Text>
                    <Text color="gray.400" mb={2}>/reservation</Text>
                  </HStack>
                  <Text color="gray.500" fontSize="sm">
                    {tier.description}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>

        <Box bg="#1A1824" borderRadius="2xl" p={8}>
          <Heading
            as="h3"
            fontSize={{ base: 'xl', md: '2xl' }}
            color="white"
            mb={6}
            textAlign="center"
          >
            All Features Included
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <List spacing={3}>
              {features.slice(0, Math.ceil(features.length / 2)).map((feature) => (
                <ListItem
                  key={feature}
                  color="gray.400"
                  fontSize="sm"
                  display="flex"
                  alignItems="center"
                >
                  <ListIcon as={CheckIcon} color="#82ca9c" mr={3} />
                  {feature}
                </ListItem>
              ))}
            </List>
            <List spacing={3}>
              {features.slice(Math.ceil(features.length / 2)).map((feature) => (
                <ListItem
                  key={feature}
                  color="gray.400"
                  fontSize="sm"
                  display="flex"
                  alignItems="center"
                >
                  <ListIcon as={CheckIcon} color="#82ca9c" mr={3} />
                  {feature}
                </ListItem>
              ))}
            </List>
          </SimpleGrid>
        </Box>

        <Box mt={16} p={6} bg="#1A1824" borderRadius="xl" textAlign="center">
          <HStack spacing={4} justify="center">
            <Text color="gray.400">Secure payments powered by</Text>
            <Text color="white" fontWeight="bold">Stripe</Text>
          </HStack>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingSection; 